import { isServer, keepPreviousData, QueryClient } from '@tanstack/react-query';
import { createRouter as createTanStackRouter } from '@tanstack/react-router';
import { setupRouterSsrQueryIntegration } from '@tanstack/react-router-ssr-query';
import { routeTree } from './routeTree.gen';
import {
	persistQueryClient,
	PersistQueryClientProvider,
	persistQueryClientSave,
	removeOldestQuery,
} from '@tanstack/react-query-persist-client';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import {
	afterSsrPayloadApplied,
	documentRenderedAt,
	PERSISTED_QUERY_CACHE_KEY,
	shouldDehydrateQuery,
	supersededDocumentQueries,
	withoutDocumentQueries,
} from './lib/query-persistence';
import type { ReactNode } from 'react';
import { gtag, sanitizeAnalyticsUrl } from '@worldcoo/ui/lib/analytics';
import { installPageViewTracking } from './lib/page-view';
import { getRuntimeAnalyticsCode } from './lib/shared';
import { createLocaleI18n, loadLandingTranslation } from './lib/i18n';
import { errorReportData, extractErrorInfo, getRequestContext, logErrorFn } from './lib/data';
import { scriptErrorLocation } from './lib/script-error-location';
import { baseUrlFromHost, portalIdFromHost } from './lib/request-host';
import {
	isApplePayReporterMounted,
	isApplePaySessionAbortError,
	isClientFaultRpcError,
	isHydrationError,
	shouldReportFrontendError,
} from './lib/frontend-error-filter';
import { describeHydrationRewrite } from './lib/hydration-rewrite-monitor';
import { installSessionActionTracking, recentBreadcrumbs } from './lib/session-actions';
import { isUnauthorizedError } from './lib/ngo-session';
import { portalLocales, type PortalLocale } from '@worldcoo/monolith/portals';
import { I18nProvider } from '@lingui/react';
import { DefaultCatchBoundary, isVersionSkewReloadInProgress } from './routes/-components/default-catch-boundary';
import { NotFound } from './routes/-components/not-found';
// From `consent-cookie`, not `usercentrics`: this module is bundled and executed on the server too, and
// that one is browser-only. The call below is guarded by `!isServer`, but an import is not.
import { readConsentCookie } from '@worldcoo/ui/lib/consent-cookie';
import {
	hasChildSegments,
	SLUG_CONTEXTS,
	toInternalSegments,
	toLocaleSegments,
	type SlugContext,
} from './lib/slug-rewrites';
import { wwwLocales } from './routes/www/-components/locales';
import { detectLocale } from '@worldcoo/monolith/core/pure';

/**
 * Client-only; NEVER read during render, where the SSR worker would hand over another request's state
 * (TEC-3607). docs/notes/portals/router.md
 */
let _clientLocaleState: { locale: string; hasLocalePrefix: boolean } | undefined;

export function updateRouterLocale(locale: string) {
	if (_clientLocaleState) {
		_clientLocaleState.locale = locale;
		_clientLocaleState.hasLocalePrefix = true;
	}
}

/** Client-only, published for `wasOurTextReplaced` alone. docs/notes/portals/router.md */
let _clientQueryClient: QueryClient | undefined;

export function clientQueryClient(): QueryClient | undefined {
	return _clientQueryClient;
}

function isPortalLocale(segment: string | undefined): segment is string {
	return !!segment && (portalLocales as readonly string[]).includes(segment);
}

/** Shared by both halves of the rewrite, so inbound and outbound URLs never disagree on the context. */
function slugContextFor(prefix: string | undefined): SlugContext {
	return prefix === 'www' ? 'www' : prefix === 'old' ? 'old' : 'portal';
}

/** The live site publishes five of the ten `portalLocales`; the rest must 404. docs/notes/portals/router.md */
function publishesLocale(prefix: string, segment: string | undefined): segment is string {
	return prefix === 'www' ? (wwwLocales as readonly string[]).includes(segment ?? '') : isPortalLocale(segment);
}

/** Never rewritten; the donation notification is host-agnostic on purpose (routes/api.donations.$donationId.ts). */
function servedAtTheRootLevel(pathname: string): boolean {
	return pathname.startsWith('/media/') || pathname.startsWith('/api/trpc') || pathname.startsWith('/api/donations/');
}

/** TODO: TanStack Start RPC rather than tRPC, whose backend headers disable query persistence; costs batching. */
export async function getRouter() {
	const queryClient = new QueryClient({
		defaultOptions: {
			queries: {
				staleTime: 60 * 1000,
				gcTime: Infinity,
				placeholderData: keepPreviousData,
				// An SSR retry only holds the Suspense boundary open; a 401 is a decided answer for the login redirect.
				retry: (failureCount, error) => !isServer && !isUnauthorizedError(error) && failureCount < 3,
			},
			// A retry of a replayed SSR rejection has no mounted observer, so it parks the query as
			// `paused` forever: skeleton stuck, no throw (TEC-3737).
			hydrate: { queries: { retry: false } },
		},
	});

	const storagePersister = createAsyncStoragePersister({
		key: PERSISTED_QUERY_CACHE_KEY,
		storage: typeof window !== 'undefined' ? window.localStorage : undefined,
		retry: removeOldestQuery,
	});

	// Document-first restore (TEC-3881); the displaced newer entries refetch after hydration. docs/notes/portals/router.md
	const superseded = new Set<string>();

	const persister = {
		...storagePersister,
		restoreClient: async () => {
			const restored = await storagePersister.restoreClient();
			if (!restored) return restored;
			const cache = queryClient.getQueryCache();
			const documentCarries = (hash: string) => cache.get(hash) !== undefined;
			for (const hash of supersededDocumentQueries(
				restored.clientState.queries,
				documentCarries,
				documentRenderedAt(cache.getAll()),
			))
				superseded.add(hash);

			return {
				...restored,
				clientState: {
					...restored.clientState,
					queries: withoutDocumentQueries(restored.clientState.queries, documentCarries),
				},
			};
		},
	};

	// The only way to drop a persisted blob, whose 24h `maxAge` never expires (TEC-3758). docs/notes/portals/router.md
	const buster = 'v6';

	const dehydrateOptions = { shouldDehydrateQuery };

	// The provider restores in a mount effect, too late for the first render; the caller picks WHEN.
	let restoreStarted = false;
	const restorePersistedCache = () => {
		if (restoreStarted) return;
		restoreStarted = true;

		const [, restored] = persistQueryClient({ queryClient, persister, buster, dehydrateOptions });
		// `persistQueryClient` only writes on LATER cache events, so without this save the blob is never written (measured).
		restored
			.then(() => {
				// Empty on a fresh document; the tree is live by now, so these refetches cannot mismatch.
				if (superseded.size > 0) {
					void queryClient.invalidateQueries({
						predicate: (query) => superseded.has(query.queryHash),
					});
				}

				return persistQueryClientSave({ queryClient, persister, buster, dehydrateOptions });
			})
			.catch(() => {
				// A cache we could not persist costs the next visit a warm start, nothing else.
			});
	};

	const serverContext = isServer ? await getRequestContext() : undefined;

	/** Read back from the server's `<html lang>`, so it agrees by construction (TEC-3689). docs/notes/portals/router.md */
	const documentLocale =
		!isServer && typeof document !== 'undefined' && document.documentElement.lang
			? detectLocale(document.documentElement.lang, portalLocales)
			: undefined;

	/** Every placeholder must equal what `hydrate()` installs, by construction. docs/notes/portals/router.md */
	const documentHost = !isServer ? window.location.host : undefined;

	const requestContext = {
		baseUrl: serverContext?.baseUrl ?? (documentHost ? baseUrlFromHost(documentHost) : ''),
		portalId: serverContext?.portalId ?? (documentHost ? portalIdFromHost(documentHost) : ''),
		locale: serverContext?.locale ?? documentLocale ?? 'es',
		isBot: serverContext?.isBot ?? false,
		isInIframe:
			serverContext?.isInIframe ?? (!isServer && new URLSearchParams(window.location.search).get('iframe') === 'true'),
		// The cookie is the one source both passes can see, so the first client render matches. See `syncConsentCookie`.
		consentDecision: serverContext?.consentDecision ?? (!isServer ? readConsentCookie() : undefined),
	};

	const localeState: { locale: string; hasLocalePrefix: boolean } = {
		locale: requestContext.locale,
		hasLocalePrefix: true,
	};
	if (!isServer) _clientLocaleState = localeState;
	if (!isServer) _clientQueryClient = queryClient;

	// Per request on the server, so concurrent renders cannot swap locales (React #418).
	const i18nInstance = createLocaleI18n();

	// `rewrite.input` mutates it before the first render, so SSR and client agree.
	const routerContext = { queryClient, i18n: i18nInstance, hasLocalePrefix: true, ...requestContext };

	await loadLandingTranslation(
		isServer ? requestContext.locale : (document.documentElement.lang as PortalLocale) || 'es',
		i18nInstance,
	);

	const router = createTanStackRouter({
		routeTree,
		context: routerContext,
		dehydrate: () => ({
			requestContext: {
				...requestContext,
				locale: detectLocale(localeState.locale, portalLocales),
				// Re-deriving it on the client risks a canonical-URL #418 (TEC-3607).
				hasLocalePrefix: localeState.hasLocalePrefix,
			},
		}),
		hydrate: (dehydrated) => {
			if (dehydrated.requestContext) {
				localeState.locale = dehydrated.requestContext.locale;
				localeState.hasLocalePrefix = dehydrated.requestContext.hasLocalePrefix;
				if (router.options.context) {
					Object.assign(router.options.context, dehydrated.requestContext);
				}
			}
		},
		defaultPreload: 'intent',
		scrollRestoration: true,
		rewrite: {
			input: ({ url }) => {
				if (servedAtTheRootLevel(url.pathname)) return url;

				const hostParts = url.hostname.split('.');
				const isMainDomain =
					hostParts[0] === 'www' ||
					(hostParts.length < 3 && hostParts.at(-1) !== 'localhost') ||
					(hostParts.length < 2 && hostParts.at(-1) === 'localhost');

				const prefix = isMainDomain ? 'www' : hostParts[0]!;
				url.pathname = `/${prefix}${url.pathname}`;

				if (prefix !== 'ngos') {
					const segments = url.pathname.split('/').filter(Boolean);
					if (publishesLocale(prefix, segments[1])) {
						localeState.locale = segments[1];
						localeState.hasLocalePrefix = true;
						// So the very first SSR pass renders the right `<html lang>` and canonical URL.
						const resolvedLocale = detectLocale(segments[1], portalLocales);
						routerContext.locale = resolvedLocale;
						routerContext.hasLocalePrefix = true;
						segments.splice(1, 1);
						url.pathname = '/' + segments.join('/');
					} else {
						localeState.hasLocalePrefix = false;
						routerContext.hasLocalePrefix = false;
					}
				}

				if (prefix !== 'ngos') {
					const context = slugContextFor(prefix);
					const segments = url.pathname.split('/').filter(Boolean);
					const routeName = segments[1] ? SLUG_CONTEXTS[context].slugToRoute[segments[1]] : undefined;
					// Only a route that DECLARES child segments is translated deeper: portal campaign paths share this rewrite.
					const nested = routeName ? hasChildSegments(routeName, context) : false;
					if (routeName && (segments.length === 2 || nested)) {
						url.pathname =
							'/' + [segments[0]!, routeName, ...toInternalSegments(routeName, segments.slice(2), context)].join('/');
					}
				}

				return url;
			},
			output: ({ url }) => {
				if (servedAtTheRootLevel(url.pathname)) return url;

				const parts = url.pathname.split('/').filter(Boolean);
				if (parts.length === 0) return url;

				const prefix = parts[0];

				if (prefix === 'ngos') {
					url.pathname = '/' + parts.slice(1).join('/') || '/';
					return url;
				}

				if (prefix !== 'ngos' && parts.length >= 2 && parts[1]) {
					const context = slugContextFor(prefix);
					const maps = SLUG_CONTEXTS[context];
					const routeName = parts[1];
					// Same gate as the input side: only a route that declares the segments below it owns them.
					const nested = hasChildSegments(routeName, context);
					if (maps.routeToSlugs[routeName] && (parts.length === 2 || nested)) {
						parts[1] = maps.routeToSlugs[routeName]![localeState.locale] ?? routeName;
						const below = toLocaleSegments(routeName, parts.slice(2), localeState.locale, context);
						parts.splice(2, parts.length - 2, ...below);
					}
				}

				const rest = parts.slice(1).join('/');
				url.pathname = localeState.hasLocalePrefix ? `/${localeState.locale}${rest ? '/' + rest : ''}` : `/${rest}`;
				return url;
			},
		},
		// React Query owns staleness, so loaders always run.
		defaultPreloadStaleTime: 0,
		// Routes own their skeletons; this catches the rest in place instead of blanking the whole app.
		defaultPendingComponent: () => null,
		Wrap: (props: { children: ReactNode }) => {
			return (
				<PersistQueryClientProvider client={queryClient} persistOptions={{ persister, buster, dehydrateOptions }}>
					{props.children}
				</PersistQueryClientProvider>
			);
		},
		defaultErrorComponent: (props) => (
			<I18nProvider i18n={i18nInstance}>
				<DefaultCatchBoundary {...props} />
			</I18nProvider>
		),
		// `NotFound` reads a Lingui title and can be reached from a route with no `I18nProvider`.
		defaultNotFoundComponent: () => (
			<I18nProvider i18n={i18nInstance}>
				<NotFound />
			</I18nProvider>
		),
	});

	setupRouterSsrQueryIntegration({
		router,
		queryClient,
		wrapQueryClient: false,
	});

	// `load-client` awaits this hook before any match, so the restore sees the document's queries (TEC-3881). docs/notes/portals/router.md
	if (!isServer) {
		const hydrateWithDocumentQueries = router.options.hydrate;
		router.options.hydrate = async (dehydrated: Parameters<NonNullable<typeof hydrateWithDocumentQueries>>[0]) => {
			await hydrateWithDocumentQueries?.(dehydrated);
			restorePersistedCache();
		};

		// Payload-less documents only: on a restored document this runs synchronously, ahead of the hook.
		if (!window.$_TSR) afterSsrPayloadApplied(restorePersistedCache);

		gtag('js', new Date());
		// The landing page_view; later ones wait for the page's title (TEC-3823, TEC-3865). docs/notes/portals/router.md
		gtag('config', getRuntimeAnalyticsCode(), {
			page_location: sanitizeAnalyticsUrl(window.location.href),
			page_title: document.title,
		});

		installPageViewTracking((onNavigate) => {
			router.history.subscribe((url) => {
				onNavigate(url.location.href);
			});
		});

		// What may be collected is strictly limited: see session-actions.ts.
		installSessionActionTracking((onNavigate) => {
			router.history.subscribe((url) => {
				onNavigate(url.location.pathname);
			});
		});

		// A deploy deletes the previous build's chunks (https://vite.dev/guide/build#load-error-handling).
		window.addEventListener('vite:preloadError', () => {
			window.location.reload();
		});

		window.addEventListener('error', (event) => {
			if (isVersionSkewReloadInProgress()) return;
			// Cross-origin script errors (reCAPTCHA, GA) carry a null error.
			if (event.error == null) return;
			// A deliberate FORBIDDEN is the caller's fault; judged on the object before it becomes strings.
			if (isClientFaultRpcError(event.error)) return;
			const errorInfo = extractErrorInfo(event.error);
			// Same gate as client.tsx: a translator signal alone misses text-only rewrites (TEC-3703).
			const rewrite = isHydrationError(errorInfo.message) ? describeHydrationRewrite(clientQueryClient()) : undefined;
			if (rewrite?.foreign) return;
			if (!shouldReportFrontendError(errorInfo.name, errorInfo.message, errorInfo.stack, errorInfo.cause)) return;
			logErrorFn({
				data: errorReportData('[GLOBAL] ', errorInfo, {
					source: 'global',
					// The only locator of a parse-time `SyntaxError`, which has no stack (TEC-3747).
					script: scriptErrorLocation(event),
					breadcrumbs: recentBreadcrumbs(),
					// Tells "pristine page" apart from "check never ran".
					counters: rewrite?.attribution,
					// See the `mismatch` note in `client.tsx`: React ships no diff for `args[]=text` in production.
					mismatch: rewrite?.mismatch,
				}),
			}).catch((logErr) => {
				console.error('Failed to log error to backend:', logErr);
			});
		});

		window.addEventListener('unhandledrejection', (event) => {
			if (isVersionSkewReloadInProgress()) return;
			if (isClientFaultRpcError(event.reason)) return;
			const errorInfo = extractErrorInfo(event.reason);
			// TEC-3696 family.
			const rewrite = isHydrationError(errorInfo.message) ? describeHydrationRewrite(clientQueryClient()) : undefined;
			if (rewrite?.foreign) return;
			// The mounted checkout reports it with its Apple Pay context (TEC-3637); this would ticket it twice.
			if (
				isApplePayReporterMounted() &&
				isApplePaySessionAbortError(errorInfo.name, errorInfo.message, errorInfo.stack)
			) {
				return;
			}
			if (!shouldReportFrontendError(errorInfo.name, errorInfo.message, errorInfo.stack, errorInfo.cause)) return;
			logErrorFn({
				data: errorReportData('[UNHANDLED REJECTION] ', errorInfo, {
					source: 'unhandledrejection',
					breadcrumbs: recentBreadcrumbs(),
					counters: rewrite?.attribution,
					// See the `mismatch` note in `client.tsx`: React ships no diff for `args[]=text` in production.
					mismatch: rewrite?.mismatch,
				}),
			}).catch((logErr) => {
				console.error('Failed to log error to backend:', logErr);
			});
		});
	}

	return router;
}
